'use client';

import type React from 'react';
import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FieldErrorMessage } from '@/components/ui/FormFields/FieldErrorMessage';
import { Label } from '@/components/ui/FormFields/Label';
import { changedFieldClassName, useIsChangedField } from '@/components/shared/FormChangeHighlightContext';
import { useRequiredField } from '@/components/shared/FormRequiredFieldsContext';

export type Option = { value: string; label: string; subLabel?: string };
export type OptionGroup = { label: string; options: Option[] };

const areNumberSetsEqual = (left: Set<number>, right: Set<number>): boolean => {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
};

type BaseDropDownProps = {
  id: string;
  label?: string;
  description?: string;
  placeholder?: string;
  options: (Option | OptionGroup)[];
  error?: string;
  labelClassName?: string;
  dropDownClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
  fieldName?: string;
  required?: boolean;
  reserveErrorMessageSpace?: boolean;
  renderOption?: (option: Option, state: { selected: boolean }) => React.ReactNode;
  renderValue?: (option: Option | null, valueCount: number) => React.ReactNode;
};

type SingleDropDownProps = BaseDropDownProps & {
  multiple?: false;
  value: string;
  onValueChange?: (value: string) => void;
};

type MultipleDropDownProps = BaseDropDownProps & {
  multiple: true;
  value: string[];
  onValueChange?: (value: string[]) => void;
};

type DropDownProps = SingleDropDownProps | MultipleDropDownProps;

const MENU_GAP_PX = 4;
const MENU_VIEWPORT_MARGIN_PX = 16;
const MENU_MAX_HEIGHT_PX = 384;
const MENU_MIN_HEIGHT_PX = 96;

type MenuPlacement = 'top' | 'bottom';

const DropDown: React.FC<DropDownProps> = ({
  id,
  label,
  placeholder = 'choose...',
  description,
  value,
  options,
  error,
  onValueChange,
  labelClassName,
  containerClassName = 'space-y-2',
  dropDownClassName,
  disabled = false,
  fieldName,
  required,
  reserveErrorMessageSpace,
  multiple = false,
  renderOption: renderOptionContent,
  renderValue,
}) => {
  const isRequired = useRequiredField(fieldName, required);
  const isChanged = useIsChangedField(fieldName);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const valueArray = useMemo(() => (Array.isArray(value) ? value : [value].filter(Boolean)), [value]);

  const flatOptions = options.flatMap((o) => ('options' in o ? o.options : [o]));

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState(false);
  const [menuLayout, setMenuLayout] = useState<{ maxHeight: number; placement: MenuPlacement }>({
    maxHeight: MENU_MAX_HEIGHT_PX,
    placement: 'bottom',
  });

  const updateMenuLayout = useCallback(() => {
    const trigger = triggerRef.current;

    if (trigger == null) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom - MENU_VIEWPORT_MARGIN_PX;
    const spaceAbove = triggerRect.top - MENU_VIEWPORT_MARGIN_PX;
    const placement: MenuPlacement = spaceBelow < MENU_MIN_HEIGHT_PX && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const availableSpace = Math.max(0, (placement === 'top' ? spaceAbove : spaceBelow) - MENU_GAP_PX);
    const maxHeight = Math.min(MENU_MAX_HEIGHT_PX, availableSpace);

    setMenuLayout((currentLayout) =>
      currentLayout.maxHeight === maxHeight && currentLayout.placement === placement
        ? currentLayout
        : { maxHeight, placement }
    );
  }, []);

  useLayoutEffect(() => {
    if (open) {
      updateMenuLayout();
    }
  }, [open, updateMenuLayout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    window.addEventListener('resize', updateMenuLayout);
    window.addEventListener('scroll', updateMenuLayout, true);

    return () => {
      window.removeEventListener('resize', updateMenuLayout);
      window.removeEventListener('scroll', updateMenuLayout, true);
    };
  }, [open, updateMenuLayout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    const groupsToExpand = new Set<number>();

    options.forEach((option, idx) => {
      if ('options' in option && option.options.some((o) => valueArray.includes(o.value))) {
        groupsToExpand.add(idx);
      }
    });
    setExpandedGroups((currentGroups) =>
      areNumberSetsEqual(currentGroups, groupsToExpand) ? currentGroups : groupsToExpand
    );
  }, [options, valueArray]);

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);

      next.has(index) ? next.delete(index) : next.add(index);

      return next;
    });
  };

  const handleValueChange = (val: string) => {
    if (!multiple) {
      (onValueChange as (value: string) => void)?.(val);
      setOpen(false);

      return;
    }

    const newValues = valueArray.includes(val) ? valueArray.filter((v) => v !== val) : [...valueArray, val];

    (onValueChange as (value: string[]) => void)?.(newValues);
  };

  const optionClassName = (isSelected: boolean, isGrouped = false) =>
    cn(
      isGrouped ? 'text-xs' : 'text-sm',
      'text-gray900 relative pl-8 py-1 cursor-pointer hover:bg-muted',
      isSelected && 'font-semibold bg-muted'
    );

  const renderOption = (option: Option, isGrouped = false) => {
    const isSelected = valueArray.includes(option.value);

    if (multiple) {
      return (
        <div
          key={option.value}
          // biome-ignore lint/a11y/useSemanticElements: The design system dropdown is a custom multi-select option, not a native option.
          role="option"
          aria-selected={isSelected}
          tabIndex={-1}
          className={optionClassName(isSelected, isGrouped)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleValueChange(option.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleValueChange(option.value);
            }
          }}
        >
          {isSelected && <Check className="absolute left-2 h-4 w-4" />}
          {renderOptionContent ? renderOptionContent(option, { selected: isSelected }) : option.label}
        </div>
      );
    }

    return (
      <div
        key={option.value}
        // biome-ignore lint/a11y/useSemanticElements: The design system dropdown is a custom single-select option, not a native option.
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        className={optionClassName(isSelected, isGrouped)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleValueChange(option.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleValueChange(option.value);
          }
        }}
      >
        {isSelected && <Check className="absolute left-2 h-4 w-4" />}
        {renderOptionContent ? renderOptionContent(option, { selected: isSelected }) : option.label}
      </div>
    );
  };

  const renderOptions = (opts: (Option | OptionGroup)[], isNested = false) =>
    opts.map((option, idx) =>
      'options' in option ? (
        <div key={option.label}>
          <button
            type="button"
            className="cursor-pointer px-3 py-1 w-full font-medium text-xs text-gray900 flex justify-between items-center hover:bg-muted"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => toggleGroup(idx)}
          >
            {option.label} <span className="ml-2">{expandedGroups.has(idx) ? '▾' : '▸'}</span>
          </button>
          {expandedGroups.has(idx) && renderOptions(option.options, true)}
        </div>
      ) : (
        renderOption(option, isNested)
      )
    );

  const displayValue = () => {
    if (!multiple) {
      const selectedOption = flatOptions.find((option) => option.value === valueArray[0]) ?? null;

      return renderValue ? renderValue(selectedOption, valueArray.length) : (selectedOption?.label ?? placeholder);
    }

    if (valueArray.length === 0) return placeholder;

    if (valueArray.length === 1) {
      const option = flatOptions.find((o) => o.value === valueArray[0]);

      return renderValue ? renderValue(option ?? null, valueArray.length) : (option?.label ?? placeholder);
    }

    return renderValue ? renderValue(null, valueArray.length) : `${valueArray.length} selected`;
  };

  return (
    <div ref={rootRef} className={cn('w-full', containerClassName)}>
      {label && (
        <div className="flex flex-col space-y-2">
          <Label
            htmlFor={id}
            required={isRequired}
            className={cn('text-primary text-xs font-semibold', labelClassName)}
          >
            {label}
          </Label>
          {description && <span className="text-gray800 text-xs">{description}</span>}
        </div>
      )}

      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          // biome-ignore lint/a11y/useSemanticElements: This is a custom combobox trigger for styled grouped and multi-select dropdowns.
          role="combobox"
          aria-controls={`${id}-listbox`}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-required={isRequired || undefined}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setOpen((currentOpen) => !currentOpen);
            }
          }}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
            'w-full border border-tablesDivider bg-inputBackground rounded-sm',
            isChanged && changedFieldClassName,
            dropDownClassName
          )}
        >
          <span className={valueArray.length ? 'text-gray900' : 'text-gray600'}>{displayValue()}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>

        {open && (
          <div
            id={`${id}-listbox`}
            // biome-ignore lint/a11y/useSemanticElements: This custom listbox supports grouped and multi-select content beyond native select rendering.
            // biome-ignore lint/a11y/useFocusableInteractive: Focus remains on the combobox trigger while options are selected by pointer/keyboard handlers.
            role="listbox"
            aria-multiselectable={multiple || undefined}
            tabIndex={-1}
            className={cn(
              'absolute z-[90] w-full min-w-[8rem] overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
              menuLayout.placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
            )}
            style={{ maxHeight: menuLayout.maxHeight }}
          >
            {renderOptions(options)}
          </div>
        )}
      </div>

      <FieldErrorMessage message={error} reserveSpace={reserveErrorMessageSpace} />
    </div>
  );
};

export default DropDown;
